import React from 'react';
import {
  TransparentBg,
  Modal,
  ModalHeader,
  OccurTitle,
  CloseModalButton,
  OccurContainer,
  OccurDetails,
  DetailsRows,
  DetailsCells,
  CorrectionsDetails,
  CorrectionsTitle,
  CorrectionsCardsDiv,
  CorrectionCard,
  CorrectionHeader,
  CorrectionRow,
  CorrectionCell,
} from 'components/Tables/CustomerPortalTable/components/OccurrenceModal/styled';
import placeholderPhoto from 'assets/images/placeholder_sem_fotos.png';

// Mock completo de uma ocorrência com todos os campos preenchidos
const MOCK_OCCURRENCE = {
  dataRegistro: '2026-06-01T09:00:00',
  dataLimiteExecucao: '2026-06-10T18:00:00',
  prioridadeDescricao: 'Alta',
  emitentes: [{ usuarioNome: 'João Alfredo' }],
  siteNome: 'LuminaTech',
  grupoAreaNome: 'Grupo Mecânico',
  subgrupoAreaNome: 'Climatização',
  areaNome: 'Sala de Máquinas 01',
  equipamentoNome: 'Arrefecedor de Água 8C7D Philco',
  sistemaEmpresaNome: 'Ar-Condicionado Central',
  setorNome: 'Manutenção Predial',
  executores: [{ usuarioNome: 'Antonio da Silva' }, { usuarioNome: 'Carlos Mendes' }],
  linkExterno: null,
  descricao:
    'Manutenção Predial para Ar-Condicionado — equipamento apresentando ruído excessivo e perda de eficiência de resfriamento. Necessário limpeza dos filtros, verificação do compressor e recarga de gás refrigerante.',
  fotos: [],
};

// Mock de correções — uma Agendada e uma Solucionada
const MOCK_CORRECTIONS = [
  {
    codigoTipoCorrecao: 4,
    tipoCorrecaoNome: 'Agendada',
    inicio: '2026-06-03T09:00:00',
    termino: '2026-06-03T11:00:00',
    custoTotal: 350.0,
    executoresNomes: 'Antonio da Silva, Carlos Mendes',
    descricao: 'Agendada limpeza completa dos filtros e verificação do compressor.',
    fotosPathsList: [],
  },
  {
    codigoTipoCorrecao: 3,
    tipoCorrecaoNome: 'Solucionada',
    inicio: '2026-06-03T09:15:00',
    termino: '2026-06-03T10:45:00',
    custoTotal: 280.5,
    executoresNomes: 'Antonio da Silva',
    descricao: 'Filtros limpos, compressor verificado e recarregado. Equipamento operando normalmente.',
    fotosPathsList: [],
  },
];

const STATUS_COLORS = [
  { text: 'Agendada', textColor: '#286892', borderColor: '#85BFE4', backgroundColor: '#CCE4FA' },
  { text: 'Solucionada', textColor: '#00715C', borderColor: '#63CAB7', backgroundColor: '#B4EDE3' },
];

const STATUS_MAP: Record<number, number> = { 4: 0, 3: 1 };

const fmt = (iso: string | null): string => {
  if (!iso) return ' ';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} • ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

interface DevOccurrenceModalProps {
  cqa: string | undefined;
  setIsOpen: (open: boolean) => void;
}

export const DevOccurrenceModal: React.FC<DevOccurrenceModalProps> = ({ cqa, setIsOpen }) => {
  const o = MOCK_OCCURRENCE;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) setIsOpen(false);
  };

  return (
    <TransparentBg onClick={handleOverlayClick}>
      <Modal height="auto">
        <ModalHeader>
          <OccurTitle>Chamado Nº {cqa}</OccurTitle>
          <CloseModalButton onClick={() => setIsOpen(false)}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M1 1L15 15" stroke="#BBBBBB" strokeWidth="2" strokeLinecap="round" />
              <path d="M15 1L1 15" stroke="#BBBBBB" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </CloseModalButton>
        </ModalHeader>

        <OccurContainer height="90vh">
          {/* Dados da ocorrência */}
          <OccurDetails>
            <img src={placeholderPhoto} alt="foto" />
            <div>
              <DetailsRows>
                <div>
                  <DetailsCells>
                    <b>Data/Hora de Abertura</b>
                    {fmt(o.dataRegistro)}
                  </DetailsCells>
                  <DetailsCells>
                    <b>Data Limite</b>
                    {fmt(o.dataLimiteExecucao)}
                  </DetailsCells>
                </div>
                <DetailsCells>
                  <b>Prioridade</b>
                  {o.prioridadeDescricao}
                </DetailsCells>
                <DetailsCells>
                  <b>Solicitante da execução</b>
                  {o.emitentes.map((e, i) => e.usuarioNome + (i < o.emitentes.length - 1 ? '; ' : ''))}
                </DetailsCells>
              </DetailsRows>

              <DetailsRows>
                <DetailsCells>
                  <b>Local / Equipamento</b>
                  {[o.siteNome, o.grupoAreaNome, o.subgrupoAreaNome, o.areaNome, o.equipamentoNome]
                    .filter(Boolean)
                    .join(' / ')}
                </DetailsCells>
              </DetailsRows>

              <DetailsRows>
                <DetailsCells>
                  <b>Sistema</b>
                  {o.sistemaEmpresaNome}
                </DetailsCells>
                <DetailsCells>
                  <b>Setor</b>
                  {o.setorNome}
                </DetailsCells>
                <DetailsCells>
                  <b>Executor</b>
                  {o.executores.map(e => e.usuarioNome).join(', ')}
                </DetailsCells>
              </DetailsRows>

              <DetailsRows>
                <DetailsCells>
                  <b>Descrição</b>
                  {o.descricao}
                </DetailsCells>
              </DetailsRows>
            </div>
          </OccurDetails>

          {/* Correções */}
          <CorrectionsDetails height="100%">
            <CorrectionsTitle>Correções realizadas</CorrectionsTitle>
            <CorrectionsCardsDiv height="100%">
              {MOCK_CORRECTIONS.map((correction, index) => {
                const colorIndex = STATUS_MAP[correction.codigoTipoCorrecao] ?? 2;
                const colors = STATUS_COLORS[colorIndex] ?? STATUS_COLORS[0];
                return (
                  <CorrectionCard key={index}>
                    <CorrectionHeader
                      backgroundColor={colors.backgroundColor}
                      textColor={colors.textColor}
                      borderColor={colors.borderColor}
                    >
                      <div>{correction.tipoCorrecaoNome}</div>
                    </CorrectionHeader>
                    <CorrectionRow>
                      <CorrectionCell>
                        <b>Data/Hora de Início</b>
                        {fmt(correction.inicio)}
                      </CorrectionCell>
                      <CorrectionCell>
                        <b>Data/Hora de Término</b>
                        {fmt(correction.termino)}
                      </CorrectionCell>
                      <CorrectionCell>
                        <b>Custo</b>
                        {`R$${correction.custoTotal.toFixed(2)}`}
                      </CorrectionCell>
                      <CorrectionCell>
                        <b>Executor</b>
                        {correction.executoresNomes.split(',').map((nome, i) => (
                          <span key={i}>{nome.trim()}</span>
                        ))}
                      </CorrectionCell>
                      <CorrectionCell>
                        <b>Descrição</b>
                        {correction.descricao}
                      </CorrectionCell>
                    </CorrectionRow>
                  </CorrectionCard>
                );
              })}
            </CorrectionsCardsDiv>
          </CorrectionsDetails>
        </OccurContainer>
      </Modal>
    </TransparentBg>
  );
};
